import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import CartButton from "@/components/CartButton";

/**
 * Cabeçalho público único, usado em todas as páginas fora da área
 * autenticada (Landing, Loja, Carrinho, Sobre, Consultar encomenda,
 * Criar, Success). Garante consistência visual e comportamental do menu
 * entre páginas — antes cada página tinha o seu próprio <nav>.
 *
 * Regras:
 * - Uma barra fixa (sticky) com: Logo · Loja · Exemplos · Sobre · Carrinho · Entrar/A minha conta · CTA.
 * - "Exemplos" navega para /#galeria (âncora na Landing) — a página inicial
 *   trata do scroll suave quando recebe o hash.
 * - O CTA "Criar a minha arte" pode ser escondido em páginas onde ele é
 *   redundante (por ex. na própria página /criar) via `hideCTA`.
 * - A auth **NÃO** expõe atalhos de admin no cabeçalho — a área privada
 *   continua acessível através de "A minha conta".
 */
export default function PublicHeader({ hideCTA = false }) {
  const location = useLocation();
  const { user } = useAuth();

  const isActive = (path, matchStartsWith = false) => {
    if (matchStartsWith) return location.pathname.startsWith(path);
    return location.pathname === path;
  };

  const linkClass = (active) =>
    `text-sm px-3 py-2 transition-colors ${
      active ? "text-white" : "text-[#8B8B93] hover:text-white"
    }`;

  return (
    <nav
      data-testid="public-header"
      className="border-b border-[#222225] h-20 flex items-center justify-between px-6 md:px-12 sticky top-0 bg-[#09090B]/90 backdrop-blur-xl z-40"
    >
      <Link to="/" className="flex items-center gap-2" data-testid="header-home">
        <Logo full className="h-14 w-auto" />
      </Link>

      <div className="flex items-center gap-1 sm:gap-2">
        <Link
          to="/loja"
          data-testid="nav-store-btn"
          className={linkClass(isActive("/loja") || isActive("/carrinho"))}
        >
          Loja
        </Link>
        <Link
          to="/exemplos"
          data-testid="nav-examples-btn"
          className={linkClass(isActive("/exemplos"))}
        >
          Exemplos
        </Link>
        <Link
          to="/sobre"
          data-testid="nav-sobre-btn"
          className={linkClass(isActive("/sobre"))}
        >
          Sobre
        </Link>
        <CartButton />
        <Link
          to={user ? "/dashboard" : "/login"}
          data-testid="nav-login-btn"
          className={linkClass(false)}
        >
          {user ? "A minha conta" : "Entrar"}
        </Link>
        {!hideCTA && (
          <Link
            to="/criar"
            data-testid="nav-cta-btn"
            className="ml-1 text-sm bg-[#FF3B00] hover:bg-[#E63500] text-black font-medium px-4 py-2 transition-colors duration-200"
          >
            Criar a minha arte
          </Link>
        )}
      </div>
    </nav>
  );
}
